import React, { useEffect, useState, useRef } from 'react';
import {
  Flex,
  Button,
  useToast,
  Badge,
  Text,
  HStack,
  VStack,
  Box
} from 'components-lib';
import { FaFileAlt, FaFlask, FaDownload, FaSave } from 'react-icons/fa';
import PastVisitsModal from "../../PastVisitsModal";
import LoadTemplateModal from './LoadTemplateModal';
import SaveTemplateModal from './SaveTemplateModal';
import PastAppoinmentModal from './PastAppoinmentModal';
import LabTestsModal from './LabTestsModal';
import { axiosInstance } from 'config/api';

/**
 * ConsultationToolbar - Main toolbar component for consultation management
 * 
 * This component provides a comprehensive toolbar interface for managing consultation
 * workflows in the healthcare application. It serves as the central hub for accessing
 * various consultation-related features like templates, patient history, and lab reports.
 * 
 * Key Features:
 * - Template Management: Load and save consultation templates
 * - Patient History: Access past visits and prescriptions  
 * - Lab Reports: View and manage laboratory test results
 * - Visit Status: Display first/last visit status dynamically
 * - Modal Integration: Seamlessly integrates with multiple modal components
 * - Real-time Data: Fetches current appointment and patient data
 * 
 * Component Architecture:
 * - Button-based interface with clear visual hierarchy
 * - Modal state management for various sub-components
 * - API integration for appointment and prescription data
 * - Responsive layout with proper spacing and grouping
 * - Error handling with toast notifications
 * 
 * Props:
 * @param {Function} setTemplateData - Callback to update template data in parent
 * @param {Object} patient - Patient information object
 * @param {string|number} patientId - Patient identifier
 * @param {Object} patientData - Extended patient data object
 * @param {Object} consultationData - Current consultation session data
 * @param {Function} onLastConsultationLoad - Callback for loading last consultation
 * @param {string|number} appointmentId - Current appointment identifier
 * @param {boolean} hasPastVisits - Flag indicating if patient has past visits
 * @param {string|number} forPastVisitIdOfPatient - Specific past visit identifier
 * @param {boolean} isPastVisitsOpen - State for past visits modal visibility
 * @param {Function} setIsPastVisitsOpen - State setter for past visits modal
 * 
 * State Management:
 * - Modal visibility states for each integrated component
 * - Patient ID resolution and caching
 * - Visit status tracking (first visit vs returning patient)
 * - Local session management for offline capabilities
 * 
 * API Integration:
 * - Fetches appointment details by ID
 * - Retrieves latest prescription data for visit status
 * - Handles authentication token management
 * - Provides fallback error handling
 * 
 * Usage:
 * ```jsx
 * <ConsultationToolbar
 *   setTemplateData={handleTemplateData}
 *   patient={currentPatient}
 *   patientId={patientId}
 *   appointmentId={appointmentId}
 *   consultationData={consultationData}
 *   isPastVisitsOpen={showPastVisits}
 *   setIsPastVisitsOpen={setShowPastVisits}
 * />
 * ```
 * 
 * Related Components:
 * - LoadTemplateModal: For loading saved consultation templates
 * - SaveTemplateModal: For creating new consultation templates
 * - PastAppoinmentModal: For viewing patient's appointment history
 * - LabTestsModal: For managing laboratory reports
 * - PastVisitsModal: For accessing previous visit details
 * 
 * @component
 * @since 1.0.0
 * @author Healthcare Team
 */
const ConsultationToolbar = ({
  setTemplateData,
  patient,
  patientId,
  setPastpatientSpecifcData,
  patientData,
  consultationData,
  onLastConsultationLoad,
  appointmentId, hasPastVisits,
  forPastVisitIdOfPatient,
  isPastVisitsOpen,
  setIsPastVisitsOpen,
  doctor_id
}) => {
  const toast = useToast();
  const [isPastModalOpen, setIsPastModalOpen] = useState(false);
  const [isLabModalOpen, setIsLabModalOpen] = useState(false);
  const [isLoadTemplateOpen, setIsLoadTemplateOpen] = useState(false);
  const [isSaveTemplateOpen, setIsSaveTemplateOpen] = useState(false);
  const [hasLocalSession] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(true);
  const [patient_ID, setPatient_ID] = useState();

  // NEW state for parent-managed past prescription data
  const [pastAppointments, setPastAppointments] = useState([]);
  const [pastPrescriptionData, setPastPrescriptionData] = useState(null);
  const [isPastDataLoading, setIsPastDataLoading] = useState(false);
  const [pastDataError, setPastDataError] = useState(null);
  const pastDataFetchedRef = useRef(false);
  const prescriptionsCacheRef = useRef(new Map());
  const appointmentCacheRef = useRef(new Map());

  // Resolve a numeric patient ID for APIs that require numbers
  // const deriveNumericPatientId = () => {
  //   const rawId = patient?.id ?? patient?.patient_id ?? patientid;
  //   if (rawId == null) return undefined;
  //   // If it's already a number, return it
  //   if (typeof rawId === 'number') return rawId;
  //   // If it's a numeric-like string, parse it; otherwise strip non-digits (e.g., PAT0001 -> 0001)
  //   const onlyDigits = String(rawId).match(/\d+/g)?.join('') || '';
  //   const parsed = onlyDigits.length > 0 ? parseInt(onlyDigits, 10) : NaN;
  //   return Number.isNaN(parsed) ? undefined : parsed;
  // };

  // const patientId = deriveNumericPatientId();

  // Check for local session on component mount
  // useEffect(() => {
  //   (async () => {
  //     if (!patientId) return;
  //     // We treat presence of any prescription for current appointment as session
  //     try {
  //       // Attempt to read appointment from patient object
  //       const appointmentId = patient?.appointment_id ?? patient?.id;
  //       let hasSession = false;
  //       if (appointmentId) {
  //         const ids = getStoredPrescriptionIds();
  //         hasSession = ids.includes(String(appointmentId));
  //         setHasLocalSession(hasSession);
  //         if (hasSession) {
  //           const data = await loadPrescriptionData(appointmentId);
  //           if (data && typeof onLoadDecrypted === 'function') {
  //             onLoadDecrypted(data);
  //           }
  //         }
  //       }
  //     } catch (e) {
  //       console.warn('Session check failed:', e);
  //     }
  //   })();
  // }, [patientId, patient, onLoadDecrypted]);



  const getPrescriptionslatest = async (patient_id, { force = false } = {}) => {
    if (!patient_id) return { success: false, error: 'Missing patient id' };

    const cacheKey = String(patient_id);
    if (!force && prescriptionsCacheRef.current.has(cacheKey)) {
      return prescriptionsCacheRef.current.get(cacheKey);
    }

    try {
      const response = await axiosInstance.get('api/prescription/getPrescriptions', {
        params: { latest: true, patient_id }
      });
      const payload = response?.data ?? { success: false };
      prescriptionsCacheRef.current.set(cacheKey, payload);
      return payload;
    } catch (error) {
      const payload = { success: false, error: error.message };
      prescriptionsCacheRef.current.set(cacheKey, payload);
      return payload;
    }
  };

  const getAppointmentById = async ({ force = false } = {}) => {
    if (!appointmentId) return { success: false, error: 'Missing appointment id' };

    const cacheKey = String(appointmentId);
    if (!force && appointmentCacheRef.current.has(cacheKey)) {
      return appointmentCacheRef.current.get(cacheKey);
    }

    try {
      const response = await axiosInstance.get('api/appointment/getAppointmentById', {
        params: { id: appointmentId }
      });
      const payload = response?.data ?? { success: false };
      appointmentCacheRef.current.set(cacheKey, payload);
      return payload;
    } catch (error) {
      const payload = { success: false, error: error.message };
      appointmentCacheRef.current.set(cacheKey, payload);
      return payload;
    }
  };



  useEffect(() => {
    let cancelled = false;
    pastDataFetchedRef.current = false;

    (async () => {
      const patientResp = await getAppointmentById();
      if (cancelled || !patientResp?.data) return;

      const resolvedPatientId = patientResp.data?.patient_id;
      setPatient_ID(resolvedPatientId);

      const resp = await getPrescriptionslatest(resolvedPatientId);
      if (cancelled) return;

      const hasPrescriptions = Array.isArray(resp?.data) && resp.data.length > 0;
      setIsFirstVisit(!hasPrescriptions);
    })();

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);


  const handlePastPrescriptionsClick = () => {


    // alert("ConsultationToolbox " + patient);
    if (!patient_ID) {
      toast({
        title: "Error",
        description: "Patient ID not found",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setIsPastModalOpen(true);
  };

  const handleLabReportsClick = () => {
    if (!patient_ID) {
      toast({
        title: "Error",
        description: "Patient ID not found",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setIsLabModalOpen(true);
  };

  const handleLoadTemplateClick = () => {
    setIsLoadTemplateOpen(true);
  };

  const handleSaveTemplateClick = () => {
    setIsSaveTemplateOpen(true);
  };


  useEffect(() => {
    const run = async () => {
      if (isFirstVisit) return;          // nothing to load
      if (!patient_ID) return;           // need resolved patient id
      if (pastDataFetchedRef.current) return; // only once

      try {
        setIsPastDataLoading(true);
        setPastDataError(null);

        const cached = prescriptionsCacheRef.current.get(String(patient_ID));
        const resp = cached || await getPrescriptionslatest(patient_ID);
        if (!resp?.success) throw new Error(resp?.error || 'Failed to load past prescriptions');

        const list = resp.data || [];
        setPastAppointments(list);

        if (list.length > 0) {
          const presId = list[0].prescription_id || list[0].id;
          if (presId) {
            try {
              const detail = await axiosInstance.get('api/prescription/getPrescriptionById', {
                params: { id: presId }
              });
              if (detail?.data?.success) {
                setPastPrescriptionData(detail.data);
                if (typeof setPastpatientSpecifcData === 'function') {
                  setPastpatientSpecifcData(detail.data);
                }
              }
            } catch (inner) {
              console.warn('Failed to load prescription detail', inner);
            }
          }
        }
        pastDataFetchedRef.current = true;
      } catch (e) {
        setPastDataError(e.message);
      } finally {
        setIsPastDataLoading(false);
      }
    };
    run();
  }, [isFirstVisit, patient_ID, setPastpatientSpecifcData]);


  return (
    <Box>
      <VStack spacing={4} align="stretch">
        {/* Session Status */}
        {/* {hasLocalSession && ( //not implemented 
          <Box p={4} bg="blue.50" borderRadius="lg" border="1px solid" borderColor="blue.200" boxShadow="sm">
            <HStack justify="space-between">
              <Text fontSize="sm" color="blue.700">
                📝 Local consultation session available
              </Text>
              <Badge colorScheme="blue" size="sm">
                Auto-saved
              </Badge>
            </HStack>
          </Box>
        )} */}

        {/* Toolbar Buttons */}
        <Flex wrap="wrap" justify="center" gap={4} p={6} bg="gray.50" borderRadius="xl" boxShadow="md">

          {
            <Button
              colorScheme={isFirstVisit ? "red" : "purple"}
              variant={isFirstVisit ? "ghost" : "solid"}
              rounded="2xl"
              px={4}
              borderColor={isFirstVisit ? "red.400" : "gray.400"}
              borderWidth="1px"
              py={3}
              my="auto"
              fontSize="md"
              transition="all 0.2s"
              onClick={() => setIsPastVisitsOpen(true)}
              fontWeight="semibold"
              align="center"
            >
              <Text fontSize="md" fontWeight="bold">
                {isFirstVisit ? 'First Visit' : 'Last Visit'}
              </Text>
            </Button>
          }
          <Button
            onClick={handlePastPrescriptionsClick}
            colorScheme="blue"
            leftIcon={<FaFileAlt style={{ marginRight: "5px" }} />}
            isDisabled={isFirstVisit || isPastDataLoading}
            size="md"
            fontWeight="semibold"
            px={6}
            _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
            transition="all 0.2s"
          >
            {isPastDataLoading ? 'Loading...' : 'Past Prescriptions'}
          </Button>
          {pastDataError && (
            <Badge colorScheme="red" variant="subtle" fontSize="sm">
              {pastDataError}
            </Badge>
          )}

          <Button
            onClick={handleLabReportsClick}
            colorScheme="green"
            leftIcon={<FaFlask style={{ marginRight: "5px" }} />}
            size="md"
            fontWeight="semibold"
            px={6}
            _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
            transition="all 0.2s"
          >
            Lab Reports
          </Button>

          {/* <Button
            onClick={handleLoadLastConsultation}
            colorScheme="purple"
            leftIcon={<FaHistory />}
          >
            Load Last Consultation
          </Button> */}

          <Button
            onClick={handleLoadTemplateClick}
            colorScheme="orange"
            leftIcon={<FaDownload style={{ marginRight: "5px" }} />}
            size="md"
            fontWeight="semibold"
            px={6}
            _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
            transition="all 0.2s"
          >
            Load Template
          </Button>

          <Button
            onClick={handleSaveTemplateClick}
            colorScheme="teal"
            leftIcon={<FaSave style={{ marginRight: "5px" }} />}
            size="md"
            fontWeight="semibold"
            px={6}
            _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
            transition="all 0.2s"
          >
            Save Template
          </Button>
          {/* <Button
              onClick={handleClearAllClick}
              colorScheme="red"
              leftIcon={<FaTrash />}
            >
              Clear All
            </Button> */}
        </Flex>
      </VStack>

      <PastVisitsModal
        isOpen={isPastVisitsOpen}
        onClose={() => setIsPastVisitsOpen(false)}
        patientId={patient_ID}
        setPastpatientSpecifcData={setPastpatientSpecifcData}
        pastAppointments={pastAppointments}
        prescriptionData={pastPrescriptionData}
        isLoading={isPastDataLoading}
        error={pastDataError}
        skipInternalFetch
      />

      {/* Modals */}
      <PastAppoinmentModal
        isOpen={isPastModalOpen}
        onClose={() => setIsPastModalOpen(false)}
        id={patient_ID}
        userName={patient?.name || 'Patient'}
      />

      <LabTestsModal
        patientid={patient_ID}
        isOpen={isLabModalOpen}
        onClose={() => setIsLabModalOpen(false)}
        id={patient_ID}
        userName={patient?.patient_id || 'Patient'}
      />

      <LoadTemplateModal
        appointmentId={appointmentId}
        patientid={patient_ID}
        isOpen={isLoadTemplateOpen}
        onClose={() => setIsLoadTemplateOpen(false)}
        setTemplateData={setTemplateData}
        doctor_id={doctor_id}
      // onTemplateApplied={(payload) => {
      //   try {
      //     if (typeof patientData?.setTemplateSyncToken === 'function') {
      //       // bump a token to allow children to re-pull from parent state
      //       patientData.setTemplateSyncToken({ t: Date.now() });
      //     }
      //   } catch (e) { console.warn(e); }
      // }}

      // patientData={patientData}
      // patientData_complaints={patientData.complaints}
      // patientData_advicesData={patientData.advicesData}
      // patientData_medicinesData={patientData.medicinesData}
      // setMedicinesData={patientData.setMedicinesData}
      // setComplaints={patientData.setComplaints}
      // setAdvicesData={patientData.setAdvicesData}
      // setNewComplaint={patientData.setNewComplaint}
      // setNewAdvice={patientData.setNewAdvice}
      />

      <SaveTemplateModal
        doctor_id={doctor_id}
        patientid={patient_ID}
        isOpen={isSaveTemplateOpen}
        onClose={() => setIsSaveTemplateOpen(false)}
        consultationData={consultationData}
      />
    </Box>
  );
};

export default ConsultationToolbar;
